import React, { useState, useEffect } from "react";
import api from "../services/api";
import { LogOutIcon } from "../components/Icons";

function Profile({ user, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get("/vendor/profile");
        setProfile(data.profile);
      } catch (err) {
        setError(err.response?.data?.msg || "Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{height:"32px",width:"180px",marginBottom:"8px"}} />
        <div className="skeleton" style={{height:"300px",borderRadius:"12px"}} />
      </div>
    );
  }

  if (error) {
    return <div style={{background:"#fee2e2",border:"1px solid #fecaca",borderRadius:"8px",padding:"16px",color:"#dc2626"}}>{error}</div>;
  }

  const InfoRow = ({ label, value, mono }) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
      <span style={{fontSize:"13px",fontWeight:"600",color:"var(--text-muted)"}}>{label}</span>
      <span style={{fontSize:"14px",fontWeight:"600",color:"var(--text)",fontFamily:mono?"monospace":"inherit"}}>{value || "—"}</span>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div className="page-header-left">
          <h2>Business Profile</h2>
          <p>Your verified business information submitted during seller registration.</p>
        </div>
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: "750",
              color: "#dc2626",
              background: "#fef2f2",
              border: "1.5px solid #fecaca",
              borderRadius: "10px",
              cursor: "pointer",
            }}
            title="Logout from Seller Panel"
          >
            <LogOutIcon size={16} color="#dc2626" />
            <span>Logout</span>
          </button>
        )}
      </div>

      <div className="row g-4">
        <div className="col-lg-8">
          <div className="card-brand" style={{padding:"28px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"16px",marginBottom:"24px",paddingBottom:"20px",borderBottom:"1px solid var(--border)"}}>
              <div style={{width:"56px",height:"56px",background:"var(--primary-light)",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px"}}>&#x1F464;</div>
              <div>
                <h4 style={{fontWeight:"800",color:"var(--dark)",margin:"0 0 4px"}}>{profile?.businessName || user?.firstName + " " + user?.lastName}</h4>
                <span className="status-pill active"><span className="status-dot" />Verified Seller</span>
              </div>
            </div>

            <div style={{marginBottom:"24px"}}>
              <div style={{fontSize:"11px",fontWeight:"700",letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--text-faint)",marginBottom:"12px"}}>BUSINESS DETAILS</div>
              {profile ? (
                <>
                  <InfoRow label="Business Name" value={profile.businessName} />
                  <InfoRow label="Business Type" value={profile.businessType} />
                  <InfoRow label="Contact Email" value={profile.email || user?.email} />
                  <InfoRow label="Contact Phone" value={profile.phone} />
                </>
              ) : (
                <>
                  <InfoRow label="Name" value={user?.firstName + " " + user?.lastName} />
                  <InfoRow label="Email" value={user?.email} />
                </>
              )}
            </div>

            {profile?.address && (
              <div style={{marginBottom:"16px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--text-faint)",marginBottom:"12px"}}>FULFILLMENT ADDRESS</div>
                <InfoRow label="Street" value={profile.address.street} />
                <InfoRow label="City" value={profile.address.city} />
                <InfoRow label="State" value={profile.address.state} />
                <InfoRow label="Pincode" value={profile.address.pincode} mono={true} />
              </div>
            )}

            {profile?.verifiedAt && (
              <div style={{marginTop:"16px",paddingTop:"16px",borderTop:"1px solid var(--border)"}}>
                <div style={{fontSize:"11px",fontWeight:"700",letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--text-faint)",marginBottom:"12px"}}>VERIFICATION</div>
                <InfoRow label="Verified On" value={new Date(profile.verifiedAt).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})} />
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card-brand" style={{padding:"24px",marginBottom:"16px"}}>
            <h6 style={{fontWeight:"700",color:"var(--text)",marginBottom:"16px"}}>Account Status</h6>
            <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:"13px",color:"var(--text-muted)"}}>Seller Status</span>
                <span className="status-pill active"><span className="status-dot" />Approved</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:"13px",color:"var(--text-muted)"}}>Account Type</span>
                <span style={{fontSize:"13px",fontWeight:"700"}}>Seller</span>
              </div>
            </div>
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                style={{
                  marginTop: "16px",
                  width: "100%",
                  padding: "9px 14px",
                  borderRadius: "10px",
                  background: "#fef2f2",
                  border: "1.5px solid #fecaca",
                  color: "#dc2626",
                  fontWeight: "750",
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "7px",
                  cursor: "pointer"
                }}
              >
                <LogOutIcon size={15} color="#dc2626" />
                <span>Logout from Seller Account</span>
              </button>
            )}
          </div>
          <div className="card-brand" style={{padding:"24px",background:"var(--primary-light)",borderColor:"rgba(168,80,80,0.2)"}}>
            <div style={{fontSize:"20px",marginBottom:"12px"}}>&#x26A0;</div>
            <h6 style={{fontWeight:"700",color:"var(--primary)",marginBottom:"8px"}}>Profile Changes</h6>
            <p style={{fontSize:"13px",color:"var(--text-muted)",marginBottom:"12px",lineHeight:"1.6"}}>To update your business profile information, please contact our seller support team.</p>
            <a href="mailto:support@imonline.shop" style={{fontSize:"13px",fontWeight:"700",color:"var(--primary)",textDecoration:"none"}}>Contact Support &#x2192;</a>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Profile;
